import Box from 'components/ui/box';
import { useCallback, useEffect, useRef } from 'react';
import { IoClose } from 'react-icons/io5';

interface Props {
  onClose: () => void;
  description: string;
}

const useFocusTrap = (ref: React.RefObject<HTMLDivElement>, isOpen: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !ref.current) return;
      event.preventDefault();

      const focusableElements = ref.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusableElements || focusableElements.length === 0) return;

      const focusableArray = Array.from(focusableElements) as HTMLElement[];
      const currentIndex = focusableArray.indexOf(document.activeElement as HTMLElement);

      let nextIndex: number;
      if (event.shiftKey) {
        nextIndex = currentIndex <= 0 ? focusableArray.length - 1 : currentIndex - 1;
      } else {
        nextIndex = currentIndex >= focusableArray.length - 1 ? 0 : currentIndex + 1;
      }

      focusableArray[nextIndex]?.focus();
    };

    const timeoutId = setTimeout(() => {
      if (ref.current) {
        const firstFocusable = ref.current.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) as HTMLElement;
        firstFocusable?.focus();
      }
    }, 50);

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, ref, onClose]);
};

export default function ModalAlert({ onClose, description }: Props) {
  const modalContentRef = useRef<HTMLDivElement>(null);

  const handleFocusTrapClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useFocusTrap(modalContentRef, true, handleFocusTrapClose);

  return (
    <div className="fixed z-20 top-0 left-0 w-full h-full bg-black bg-opacity-80 flex flex-col items-center justify-center">
      <div ref={modalContentRef} role="dialog" aria-modal="true" aria-labelledby="minha-equipe-modal-alert-title">
        <Box className="relative bg-oxford-blue border-electric-blue p-1 lg:p-8 flex flex-col w-[330px] justify-center items-center border-opacity-100 bg-opacity-100">
          <button
            className="absolute -top-5 -right-5 button-close bg-slate-blue rounded-full p-1"
            onClick={() => onClose()}
            aria-label="Fechar modal"
          >
            <IoClose size={30} className="text-white" aria-hidden="true" />
          </button>

          <div>
            <h1 tabIndex={0} id="minha-equipe-modal-alert-title" className="text-yellow text-center text-lg mb-4">
              Ops!
            </h1>

            <p tabIndex={0}>{description}</p>
          </div>
        </Box>
      </div>
    </div>
  );
}

